import { useCallback, useEffect, useMemo, useState } from 'react';
import { PageShell } from '../layout/page-shell.jsx';
import { api, ApiError } from '../../api/index.js';
import { PocListenerPanel } from './poc-listener-panel.jsx';
import { useToast } from '../ui/toast.jsx';
import { formatUtcInTimezone } from '../../lib/time.js';

/**
 * PoC live sessions — read-only operator view of the LiveKit rooms
 * currently active on the local mesh node.
 *
 * Phase 2.5 step 1 of the PoC track. Polls /api/poc/sessions every
 * 5 s. Per room shows: channel name + mode + recording flag + PTT
 * max (if any), and per-participant: callsign + unit + DMR ID +
 * state (publishing / listening / queued) + join time. For simplex
 * channels also shows floor controller state (active speaker +
 * queue) — answers «who's transmitting right now?» at a glance.
 *
 * No mutation paths in this step. Recordings browser + admin floor
 * override come with Phase 3.7.
 */

const POLL_MS = 5000;

function fmtRelTime(ms) {
  if (!ms || ms <= 0) return '—';
  const dt = Math.max(0, Date.now() - ms);
  const s  = Math.floor(dt / 1000);
  if (s < 60)   return `${s}s`;
  const m  = Math.floor(s / 60);
  if (m < 60)   return `${m}m`;
  const h  = Math.floor(m / 60);
  if (h < 24)   return `${h}h ${m % 60}m`;
  const d  = Math.floor(h / 24);
  return `${d}d ${h % 24}h`;
}

function MetricCard({ label, value, tone = 'ink', icon }) {
  const cls =
        tone === 'lime'  ? 'text-lime-700'
      : tone === 'sky'   ? 'text-sky-700'
      : tone === 'amber' ? 'text-amber-700'
      : tone === 'rose'  ? 'text-rose-700'
      :                    'text-ink';
  return (
    <div className="rounded-2xl border border-line bg-white px-4 py-3 shadow-card">
      <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-mist">
        {icon ? <i className={`fa-solid ${icon}`} aria-hidden="true" /> : null}
        <span>{label}</span>
      </div>
      <div className={`mt-1 text-2xl font-semibold tabular-nums ${cls}`}>{value}</div>
    </div>
  );
}

function ModeBadge({ mode }) {
  if (!mode) return null;
  const cls = mode === 'simplex'
    ? 'bg-amber-50 text-amber-700 ring-1 ring-amber-200'
    : 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200';
  const label = mode === 'simplex' ? 'SIMPLEX' : 'DUPLEX';
  return (
    <span className={`inline-flex items-center rounded-md px-1.5 py-0.5 text-[10px] font-bold tracking-wide ${cls}`}>
      {label}
    </span>
  );
}

function RecBadge({ on }) {
  if (!on) return null;
  return (
    <span className="inline-flex items-center gap-1 rounded-md bg-rose-50 px-1.5 py-0.5 text-[10px] font-bold tracking-wide text-rose-700 ring-1 ring-rose-200">
      <i className="fa-solid fa-circle text-[7px]" aria-hidden="true"></i>
      REC
    </span>
  );
}

function PttMaxBadge({ sec }) {
  if (!sec || sec <= 0) return null;
  return (
    <span className="inline-flex items-center gap-1 rounded-md bg-sky-50 px-1.5 py-0.5 text-[10px] font-medium text-sky-700 ring-1 ring-sky-200">
      <i className="fa-solid fa-stopwatch text-[10px]" aria-hidden="true"></i>
      PTT max {sec}s
    </span>
  );
}

function TimeBadge({ ms, icon = 'fa-clock' }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-1.5 py-0.5 text-[11px] font-medium tabular-nums text-slate-700 ring-1 ring-slate-200">
      <i className={`fa-solid ${icon} text-[10px]`} aria-hidden="true" />
      {fmtRelTime(ms)}
    </span>
  );
}

function ParticipantRow({ p, isSpeaker, isQueued, queuePos }) {
  const person = p.person;
  const callsign = person?.callsign || p.name || (p.identity ? p.identity.slice(0, 8) : '—');
  const unit     = person?.unit || null;
  const dmrId    = Number.isFinite(person?.dmrId) ? person.dmrId : null;

  let stateColor = 'text-mist';
  let stateText = 'Listening';
  let stateIcon = 'fa-headphones';
  if (isSpeaker) {
    stateColor = 'text-emerald-600';
    stateText = 'Speaking';
    stateIcon = 'fa-microphone';
  } else if (p.publishedAudio) {
    stateColor = 'text-blue-600';
    stateText = 'Mic on';
    stateIcon = 'fa-microphone-lines';
  } else if (isQueued) {
    stateColor = 'text-amber-600';
    stateText = `Queued (#${queuePos + 1})`;
    stateIcon = 'fa-hourglass-half';
  }

  return (
    <tr className="border-b border-line last:border-b-0 hover:bg-slate-50">
      <td className="px-3 py-2">
        <div className="flex items-center gap-2">
          <i className={`fa-solid ${stateIcon} ${stateColor} text-[13px] w-4`} aria-hidden="true"></i>
          <div className="font-medium text-ink">{callsign}</div>
        </div>
      </td>
      <td className="px-3 py-2 text-sm text-mist">{unit || '—'}</td>
      <td className="px-3 py-2 text-sm font-mono text-mist tabular-nums">{dmrId ?? '—'}</td>
      <td className="px-3 py-2">
        <span className={`text-sm font-medium ${stateColor}`}>{stateText}</span>
        {p.publishedVideo ? (
          <span className="ml-2 inline-flex items-center gap-1 rounded bg-violet-50 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-violet-700 ring-1 ring-violet-200">
            <i className="fa-solid fa-video text-[10px]" aria-hidden="true"></i>
            video
          </span>
        ) : null}
      </td>
      <td className="px-3 py-2"><TimeBadge ms={p.joinedAtMs} /></td>
    </tr>
  );
}

function SessionCard({ session, onListen, isAdmin }) {
  const { room, channel, floor, participants } = session;

  const speakerId = floor?.activeSpeaker || null;
  const queue     = Array.isArray(floor?.queue) ? floor.queue : [];

  return (
    <div className="rounded-2xl border border-line bg-white shadow-card">
      <div className="flex items-start justify-between gap-3 border-b border-line px-4 py-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="truncate text-base font-semibold text-ink">
              {channel.name}
            </h3>
            <ModeBadge mode={channel.pocMode} />
            <RecBadge on={channel.pocRecording} />
            <PttMaxBadge sec={channel.pocPttMaxSec} />
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-mist">
            <span>{participants.length} {participants.length === 1 ? 'participant' : 'participants'}</span>
            <span>·</span>
            <span>Room age</span>
            <TimeBadge ms={room.creationTime} icon="fa-tower-broadcast" />
          </div>
        </div>
        {isAdmin && participants.length > 0 ? (
          <button
            type="button"
            onClick={() => onListen?.(channel)}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-xl border border-violet-300 bg-violet-50 px-3 py-1.5 text-xs font-medium text-violet-700 shadow-sm hover:bg-violet-100"
            title="Silently listen to this channel (hidden from participants)"
          >
            <i className="fa-solid fa-headphones" aria-hidden="true"></i>
            Listen
          </button>
        ) : null}
      </div>

      {floor && (speakerId || queue.length > 0) ? (
        <div className="border-b border-line bg-slate-50 px-4 py-2 text-xs">
          {speakerId ? (
            <div className="font-medium text-emerald-700">
              <i className="fa-solid fa-tower-broadcast mr-1.5" aria-hidden="true"></i>
              Floor: <span className="font-mono">{speakerId}</span>
            </div>
          ) : null}
          {queue.length > 0 ? (
            <div className="mt-0.5 text-mist">
              <i className="fa-solid fa-list-ol mr-1.5" aria-hidden="true"></i>
              Queue: <span className="font-mono">{queue.join(' → ')}</span>
            </div>
          ) : null}
        </div>
      ) : null}

      {participants.length === 0 ? (
        <div className="px-4 py-6 text-center text-sm text-mist">
          Empty room
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-line bg-slate-50 text-left text-xs font-semibold uppercase tracking-wider text-mist">
              <tr>
                <th className="px-3 py-2.5">Callsign</th>
                <th className="px-3 py-2.5">Unit</th>
                <th className="px-3 py-2.5">DMR ID</th>
                <th className="px-3 py-2.5">Status</th>
                <th className="px-3 py-2.5">In room</th>
              </tr>
            </thead>
            <tbody>
              {participants.map((p) => {
                const isSpeaker = speakerId && p.identity === speakerId;
                const qIdx = queue.indexOf(p.identity);
                const isQueued = qIdx >= 0;
                return (
                  <ParticipantRow
                    key={p.sid || p.identity}
                    p={p}
                    isSpeaker={isSpeaker}
                    isQueued={isQueued}
                    queuePos={qIdx}
                  />
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function PocSessionsPage({ currentUser }) {
  const { error: showError } = useToast();
  const isAdmin = currentUser?.role === 'admin';
  const tz = currentUser?.timezone || 'UTC';
  const [sessions, setSessions]         = useState([]);
  const [channelCounts, setChannelCounts] = useState({ simplex: 0, duplex: 0, total: 0, recording: 0 });
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState(null);
  const [lastTs, setLastTs]             = useState(null);
  // Active listener: {token, wsUrl, channelName, channelUuid} | null.
  // Only one at a time — opening a second swaps the first.
  const [listener, setListener]         = useState(null);

  const startListening = useCallback(async (channel) => {
    if (!isAdmin) return;
    try {
      const out = await api.poc.startListening(channel.uuid);
      if (!out?.token || !out?.url) throw new Error('invalid listener token');
      setListener({
        token: out.token,
        wsUrl: out.url,
        channelName: out.channelName || channel.name,
        channelUuid: channel.uuid,
      });
    } catch (e) {
      const msg = e?.message || String(e);
      showError(`Listening failed: ${msg}`);
    }
  }, [isAdmin, showError]);

  const stopListening = useCallback(() => setListener(null), []);

  const fetchSessions = useCallback(async () => {
    try {
      const resp = await api.poc.listSessions();
      setSessions(Array.isArray(resp?.sessions) ? resp.sessions : []);
      setChannelCounts(resp?.channelCounts || { simplex: 0, duplex: 0, total: 0, recording: 0 });
      setLastTs(Number(resp?.ts) || Date.now());
      setError(resp?.error || null);
    } catch (e) {
      if (e instanceof ApiError) {
        setError(e.message);
      } else {
        setError(String(e?.message || e));
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSessions();
    const t = setInterval(fetchSessions, POLL_MS);
    return () => clearInterval(t);
  }, [fetchSessions]);

  const totalParticipants = useMemo(
    () => sessions.reduce((sum, s) => sum + (s.participants?.length || 0), 0),
    [sessions],
  );

  // ISO UTC string for the time-formatter shared with the rest of
  // the app (date + time + viewer-TZ suffix, e.g. «10:54:11 EEST»).
  const lastTsLabel = lastTs
    ? formatUtcInTimezone(new Date(lastTs).toISOString(), tz)
    : '—';

  return (
    <PageShell
      icon="fa-walkie-talkie"
      title="PoC Sessions"
      subtitle="Live LiveKit rooms on this node. Refreshes every 5 seconds."
      actions={
        <div className="flex items-center gap-3">
          <span className="text-xs text-mist tabular-nums">
            Last refresh: <span className="font-medium text-ink">{lastTsLabel}</span>
          </span>
          <button
            type="button"
            onClick={fetchSessions}
            className="inline-flex items-center gap-2 rounded-xl border border-line bg-white px-3 py-1.5 text-xs font-medium text-ink shadow-sm hover:bg-slate-50"
          >
            <i className="fa-solid fa-arrows-rotate" aria-hidden="true" />
            Refresh
          </button>
        </div>
      }
    >
      {error ? (
        <div className="mb-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <i className="fa-sharp fa-solid fa-triangle-exclamation mr-2" aria-hidden="true" />
          {error}
        </div>
      ) : null}

      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <MetricCard label="Active sessions"  value={sessions.length}        icon="fa-tower-broadcast" tone="ink" />
        <MetricCard label="Participants"     value={totalParticipants}      icon="fa-users"           tone="ink" />
        <MetricCard label="Simplex channels" value={channelCounts.simplex}  icon="fa-walkie-talkie"   tone="amber" />
        <MetricCard label="Duplex channels"  value={channelCounts.duplex}   icon="fa-headset"         tone="lime" />
        <MetricCard label="Recording channels" value={channelCounts.recording} icon="fa-circle-dot"   tone="rose" />
        <MetricCard label="PoC channels (total)" value={channelCounts.total} icon="fa-list-check"     tone="sky" />
      </div>

      {loading ? (
        <div className="rounded-2xl border border-dashed border-line bg-white px-4 py-12 text-center text-mist shadow-card">
          <i className="fa-solid fa-spinner fa-spin mr-2" aria-hidden="true" />
          Loading…
        </div>
      ) : sessions.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-line bg-white px-4 py-12 text-center shadow-card">
          <i className="fa-solid fa-walkie-talkie mb-3 text-3xl text-mist" aria-hidden="true"></i>
          <div className="text-base font-medium text-ink">No active PoC sessions</div>
          <div className="mt-1 text-sm text-mist">
            Rooms appear automatically when an operator opens a PoC channel in TMS AMC.
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {sessions.map((s) => (
            <SessionCard
              key={s.room.sid || s.room.name}
              session={s}
              isAdmin={isAdmin}
              onListen={startListening}
            />
          ))}
        </div>
      )}

      {listener ? (
        <PocListenerPanel
          token={listener.token}
          wsUrl={listener.wsUrl}
          channelName={listener.channelName}
          onClose={stopListening}
        />
      ) : null}
    </PageShell>
  );
}
